/**
 * SOLOMON — useMemoryStatus hook
 * Day 7: Polls /api/memory/status for embedding coverage.
 *        Opens an SSE stream during backfill for live progress.
 * Day 9: Uses authFetch to send JWT on all requests.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const POLL_INTERVAL_IDLE    = 30_000; // Poll every 30s when idle
const POLL_INTERVAL_ACTIVE  =  3_000; // Poll every 3s during backfill

export function useMemoryStatus() {
  const [status, setStatus]         = useState(null);   // { embedded, pending, total, percent, backfill }
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [error, setError]           = useState(null);
  const eventSourceRef              = useRef(null);
  const pollTimerRef                = useRef(null);

  const { authFetch, token } = useAuth();

  // ── fetch status ────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    try {
      const res  = await authFetch(`${API_BASE}/api/memory/status`);
      const data = await res.json();
      if (data.ok !== false) {
        setStatus(data);
        setIsBackfilling(data.backfill?.running ?? false);
        setError(null);
      }
    } catch (err) {
      // Silently swallow — memory status is non-critical
      console.warn('[Solomon/Memory] Status fetch failed:', err.message);
    }
  }, [authFetch]);

  // ── trigger backfill ────────────────────────────────────────
  const startBackfill = useCallback(async () => {
    try {
      setIsBackfilling(true);
      await authFetch(`${API_BASE}/api/memory/backfill`, { method: 'POST' });
      openSSEStream();
    } catch (err) {
      setError('Failed to start backfill. Check backend connection.');
      setIsBackfilling(false);
    }
  }, [authFetch]); // eslint-disable-line

  // ── SSE stream ───────────────────────────────────────────────
  // Note: EventSource doesn't support custom headers, so we pass
  // the token as a query param for the backfill stream endpoint.
  const openSSEStream = useCallback(() => {
    // Close any existing stream
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Pass token as query param since EventSource can't set headers
    const streamUrl = token
      ? `${API_BASE}/api/memory/backfill/stream?token=${encodeURIComponent(token)}`
      : `${API_BASE}/api/memory/backfill/stream`;

    const es = new EventSource(streamUrl);
    eventSourceRef.current = es;

    es.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        setStatus((prev) => prev ? { ...prev, backfill: data } : { backfill: data });
        setIsBackfilling(data.running ?? false);

        if (data.done || !data.running) {
          es.close();
          eventSourceRef.current = null;
          // Final status refresh
          setTimeout(fetchStatus, 500);
        }
      } catch {
        // Ignore parse errors
      }
    };

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      setIsBackfilling(false);
      fetchStatus();
    };
  }, [fetchStatus, token]);

  // ── polling ─────────────────────────────────────────────────
  useEffect(() => {
    fetchStatus(); // Initial fetch

    const scheduleNext = () => {
      const interval = isBackfilling ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_IDLE;
      pollTimerRef.current = setTimeout(async () => {
        await fetchStatus();
        scheduleNext();
      }, interval);
    };

    scheduleNext();

    return () => {
      clearTimeout(pollTimerRef.current);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [fetchStatus, isBackfilling]);

  return {
    status,
    isBackfilling,
    error,
    startBackfill,
    refetch: fetchStatus,
  };
}
